import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { loadAssessment, updateAssessmentStatus, upgradeToFullAssessment } from "../api/client";
import { useTheme } from "../context/ThemeContext";
import { normalizeParticipantToken } from "../utils/legacyParticipantPaths";
import AssessmentPage from "./AssessmentPage";
import CompletedPage from "./CompletedPage";
import ContinueFullPage from "./ContinueFullPage";
import PreSurveyPage from "./PreSurveyPage";
import SurveyPausePage from "./SurveyPausePage";
import UpgradeOfferPage from "./UpgradeOfferPage";
import WelcomePage from "./WelcomePage";
import {
  countQuestionsInSteps,
  countRemainingFullQuestions,
} from "../utils/assessmentIntroMeta";
import { needsPreSurveyBeforeFull, preSurveyStillPending } from "../utils/preSurvey";
import { brandColor, brandCssVars } from "../utils/participantBranding";
import { normalizeParticipantAssessmentType } from "../utils/participantAssessmentType";

async function resolveFullSurveyToken(liteToken, existingFullToken) {
  if (existingFullToken) return existingFullToken;
  const res = await upgradeToFullAssessment(liteToken);
  const newToken = res.data?.full_survey_token;
  if (!newToken) {
    throw new Error("Could not start full assessment.");
  }
  return newToken;
}

function ParticipantFlowInner() {
  const { token: rawToken } = useParams();
  const token = normalizeParticipantToken(rawToken);
  const navigate = useNavigate();
  const [phase, setPhase] = useState("loading");
  const [userData, setUserData] = useState(null);
  const [companyName, setCompanyName] = useState("");
  const [assessmentTrack, setAssessmentTrack] = useState("lite");
  const [inviteFlow, setInviteFlow] = useState("v2_lite");
  const [fullSurveyToken, setFullSurveyToken] = useState(null);
  const [completedVariant, setCompletedVariant] = useState("lite");
  const [liteSurveyToken, setLiteSurveyToken] = useState(null);
  const [liteReportAvailable, setLiteReportAvailable] = useState(false);
  const [offerLiteReport, setOfferLiteReport] = useState("Y");
  const [assessmentLoad, setAssessmentLoad] = useState(null);
  const [error, setError] = useState(null);
  const [preSurveyReturn, setPreSurveyReturn] = useState("assessment");
  const [preSurveyVariant, setPreSurveyVariant] = useState("before");

  const applyLoadResult = useCallback((res) => {
    setAssessmentLoad(res);
    const ud = res.data.user_data || {};
    const apiPhase = res.data.participant_phase;
    const track = res.data.assessment_track || "full";
    const flow = res.data.invite_flow || (track === "lite" ? "v2_lite" : "v1_full");

    setUserData(ud);
    setCompanyName(ud.company?.company_name || "");
    setAssessmentTrack(track);
    setInviteFlow(flow);
    setFullSurveyToken(res.data.full_survey_token || null);
    setLiteSurveyToken(res.data.lite_survey_token || null);
    setLiteReportAvailable(Boolean(res.data.lite_report_available));
    setOfferLiteReport(res.data.offer_lite_report === "N" ? "N" : "Y");

    if (apiPhase === "completed") {
      setCompletedVariant(
        flow === "v1_full" || flow === "v2_full_continuation" || track === "full"
          ? "full"
          : "lite"
      );
      setPhase("completed");
    } else if (apiPhase === "upgrade_offer") {
      setPhase("upgrade_offer");
    } else if (apiPhase === "continue_full") {
      setPhase("continue_full");
    } else if (apiPhase === "assessment") {
      setPhase("assessment");
    } else if (apiPhase === "welcome") {
      setPhase("welcome");
    } else {
      setPhase("assessment");
    }
  }, []);

  const openFullAssessment = useCallback(
    async (liteToken = token, existingFullToken = fullSurveyToken) => {
      const nextToken = await resolveFullSurveyToken(liteToken, existingFullToken);
      navigate(`/a/${nextToken}`, { replace: true });
    },
    [token, fullSurveyToken, navigate]
  );

  const introCounts = useMemo(() => {
    const data = assessmentLoad?.data || {};
    const steps = data.survey_questions || [];
    const liteIds = data.lite_question_ids || [];
    const isContinuation =
      inviteFlow === "v2_full_continuation" ||
      (assessmentTrack === "full" && liteIds.length > 0);

    const questionCount =
      typeof data.question_count === "number"
        ? data.question_count
        : countQuestionsInSteps(steps);
    const remainingFromApi =
      typeof data.remaining_question_count === "number"
        ? data.remaining_question_count
        : null;
    const remaining =
      remainingFromApi != null
        ? remainingFromApi
        : isContinuation
          ? countRemainingFullQuestions(steps, liteIds)
          : questionCount;
    const fullCount =
      typeof data.full_question_count === "number"
        ? data.full_question_count
        : isContinuation
          ? countQuestionsInSteps(steps)
          : questionCount;

    return {
      // Welcome chips: for LITE→full continuation show remaining, else this survey's count
      questionCount: isContinuation ? remaining : questionCount,
      remainingCount: remaining,
      fullCount,
    };
  }, [assessmentLoad, inviteFlow, assessmentTrack]);

  const branding = useMemo(() => assessmentLoad?.data?.branding || {}, [assessmentLoad]);

  const assessmentType = useMemo(
    () => normalizeParticipantAssessmentType(assessmentLoad?.data?.assessment_type, branding),
    [assessmentLoad, branding]
  );

  useEffect(() => {
    const color = brandColor(branding);
    const root = document.documentElement;
    root.style.setProperty("--brand-primary", color);
    return () => {
      root.style.removeProperty("--brand-primary");
    };
  }, [branding]);

  useEffect(() => {
    if (!token) return;
    setPhase("loading");
    setError(null);
    let cancelled = false;
    (async () => {
      try {
        const res = await loadAssessment(token);
        if (cancelled) return;
        applyLoadResult(res);
      } catch (e) {
        if (cancelled) return;
        if (e.code === 403 || e.payload?.data?.paused) {
          setPhase("paused");
          return;
        }
        setError(e.message || "This assessment link is not available.");
        setPhase("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, applyLoadResult]);

  const { theme } = useTheme();

  function renderPhase() {
    if (!token) {
      return <p className="p-8 text-center text-red-600">Invalid assessment link.</p>;
    }
    if (phase === "loading") {
      return (
        <section className="min-h-screen flex items-center justify-center">
          <p className="text-brand font-medium">Loading…</p>
        </section>
      );
    }
    if (phase === "paused") return <SurveyPausePage />;
    if (phase === "error") {
      return <p className="p-8 text-center text-red-600 max-w-md mx-auto">{error}</p>;
    }
    if (phase === "completed") {
      return (
        <CompletedPage
          variant={completedVariant}
          surveyToken={token}
          fullSurveyToken={fullSurveyToken}
          offerLiteReport={offerLiteReport === "Y"}
          assessmentType={assessmentType}
          branding={branding}
          preSurveyPending={preSurveyStillPending(userData)}
          onCompleteBenchmark={() => {
            setPreSurveyReturn("completed");
            setPreSurveyVariant("after");
            setPhase("pre_survey");
          }}
          onOpenFullAssessment={
            completedVariant === "lite" ? () => openFullAssessment(token) : undefined
          }
        />
      );
    }
    if (phase === "upgrade_offer") {
      return (
        <UpgradeOfferPage
          surveyToken={token}
          fullSurveyToken={fullSurveyToken}
          offerLiteReport={offerLiteReport === "Y"}
          remainingQuestionCount={introCounts.remainingCount}
          assessmentType={assessmentType}
          branding={branding}
          onContinueFull={() => openFullAssessment(token, fullSurveyToken)}
          onLater={() => { setCompletedVariant("lite"); setPhase("completed"); }}
        />
      );
    }
    if (phase === "continue_full") {
      const showPreSurvey = needsPreSurveyBeforeFull({
        userData,
        assessmentTrack,
        inviteFlow,
      });
      return (
        <ContinueFullPage
          fullSurveyToken={fullSurveyToken}
          liteSurveyToken={liteSurveyToken || token}
          liteReportAvailable={liteReportAvailable}
          remainingQuestionCount={introCounts.remainingCount}
          preSurveyPending={showPreSurvey}
          assessmentType={assessmentType}
          branding={branding}
          onPreSurvey={() => {
            setPreSurveyReturn("continue_full");
            setPreSurveyVariant("before");
            setPhase("pre_survey");
          }}
          onContinueFull={() => openFullAssessment(token, fullSurveyToken)}
        />
      );
    }
    if (phase === "pre_survey") {
      return (
        <PreSurveyPage
          surveyToken={token}
          userData={userData}
          companyName={companyName}
          branding={branding}
          variant={preSurveyVariant}
          onComplete={() => {
            setUserData((prev) => ({ ...(prev || {}), pre_survey: "N" }));
            if (preSurveyReturn === "assessment") {
              setPhase("assessment");
              return;
            }
            if (preSurveyReturn === "continue_full") {
              setPhase("continue_full");
              return;
            }
            setPhase("completed");
          }}
          onSkip={
            preSurveyVariant === "before" && preSurveyReturn === "assessment"
              ? async () => {
                  try {
                    await updateAssessmentStatus(token, "Pending");
                  } catch { /* continue */ }
                  setPhase("assessment");
                }
              : undefined
          }
        />
      );
    }
    if (phase === "welcome") {
      return (
        <WelcomePage
          key={token}
          surveyToken={token}
          userData={userData}
          companyName={companyName}
          branding={branding}
          assessmentTrack={assessmentTrack}
          inviteFlow={inviteFlow}
          questionCount={introCounts.questionCount}
          assessmentType={assessmentType}
          onPreSurvey={() => {
            setPreSurveyReturn("assessment");
            setPreSurveyVariant("before");
            setPhase("pre_survey");
          }}
          onStart={() => setPhase("assessment")}
        />
      );
    }
    return (
      <AssessmentPage
        key={token}
        surveyToken={token}
        assessmentTrack={assessmentTrack}
        initialLoadResponse={assessmentLoad}
        assessmentType={assessmentType}
        onLiteCompleted={() => setPhase("upgrade_offer")}
        onFullCompleted={() => { setCompletedVariant("full"); setPhase("completed"); }}
      />
    );
  }

  /* display:contents keeps this div invisible to layout but lets
     [data-theme="dark"] CSS selectors cascade into all child components */
  return (
    <div data-theme={theme} style={{ display: "contents", ...brandCssVars(branding) }}>
      {renderPhase()}
    </div>
  );
}

export default function ParticipantFlow() {
  return <ParticipantFlowInner />;
}
